import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { AdminSidebar } from '../../components/Sidebar';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const FORMAT_INFO = {
  liga:     { label: 'Liga',       color: '#1A56DB', bg: '#E8F0FE', desc: 'Kumulative Punkte über alle Runden' },
  knockout: { label: 'K.O.-System', color: '#8B6A1C', bg: '#FBF5E6', desc: 'Viertelfinale → Halbfinale → Finale' },
};

const STATUS_INFO = {
  active:   { label: 'Aktiv',         color: '#059669', bg: '#ECFDF5' },
  finished: { label: 'Abgeschlossen', color: '#6b7280', bg: '#F3F4F6' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('de-CH') : '—';

export default function Tournaments() {
  const { hasFeature } = useAuth();
  const isPremium = hasFeature('duels');

  const [tournaments, setTournaments] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', format: 'liga', description: '' });
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/admin/tournaments').then(r => setTournaments(r.data.tournaments));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true); setErr('');
    try {
      const r = await api.post('/admin/tournaments', form);
      setTournaments(p => [r.data.tournament, ...(p || [])]);
      setShowCreate(false);
      setForm({ name: '', format: 'liga', description: '' });
    } catch (e) {
      setErr(e.response?.data?.error || 'Fehler');
    } finally { setCreating(false); }
  };

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 26 }}>Turniere</h1>
          {isPremium ? (
            <button className="btn btn-primary" onClick={() => setShowCreate(p => !p)}>
              {showCreate ? 'Abbrechen' : '+ Turnier erstellen'}
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, background: '#8B6A1C22', color: '#8B6A1C', borderRadius: 4, padding: '3px 8px', fontWeight: 700 }}>
                Premium
              </span>
              <button className="btn btn-primary" onClick={() => setShowCreate(p => !p)}>
                {showCreate ? 'Abbrechen' : '+ Turnier erstellen'}
              </button>
            </div>
          )}
        </div>

        {/* Premium upgrade notice for non-premium */}
        {!isPremium && showCreate && (
          <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid #8B6A1C', background: '#FBF5E6' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#8B6A1C', marginBottom: 8 }}>
              Turniere sind nur im Premium-Plan verfügbar
            </div>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
              Mit dem Premium-Plan können Sie Liga- und K.O.-Turniere über mehrere Runden und Moscheen hinweg organisieren.
            </p>
            <a href="/admin/profile#plan" className="btn btn-primary" style={{ textDecoration: 'none' }}>
              Jetzt auf Premium upgraden →
            </a>
          </div>
        )}

        {/* Create form — Premium only */}
        {isPremium && showCreate && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 18 }}>Neues Turnier</h3>
            <form onSubmit={create}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-input" placeholder="z.B. Ramadan-Quiz-Liga 2026"
                  value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
              </div>

              <div className="form-group">
                <label className="form-label">Format *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {Object.entries(FORMAT_INFO).map(([key, info]) => (
                    <button key={key} type="button"
                      onClick={() => setForm(p => ({ ...p, format: key }))}
                      style={{
                        padding: '14px 16px', borderRadius: 10, textAlign: 'left',
                        border: `2px solid ${form.format === key ? info.color : 'var(--border)'}`,
                        background: form.format === key ? info.bg : 'var(--surface)',
                        cursor: 'pointer',
                      }}>
                      <div style={{ fontWeight: 700, color: form.format === key ? info.color : 'var(--text)', marginBottom: 4 }}>
                        {info.label}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>{info.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.format === 'knockout' && (
                <div className="alert" style={{ background: '#FEF9E7', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#92400E' }}>
                  Im K.O.-Format fügen Sie nach jeder Runde die Spielsitzung manuell hinzu und definieren die Phase (z.B. "Viertelfinale").
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Beschreibung (optional)</label>
                <textarea className="form-input" rows={2} placeholder="z.B. Ramadan 2026 — jeden Freitag nach dem Gebet"
                  value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>

              {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

              <button className="btn btn-primary" type="submit" disabled={creating || !form.name.trim()}>
                {creating ? 'Erstellen...' : 'Turnier erstellen'}
              </button>
            </form>
          </div>
        )}

        {!tournaments ? (
          <div className="spinner-wrap"><div className="spinner" /></div>
        ) : tournaments.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <Trophy size={40} strokeWidth={1.5} color="var(--text3)" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Noch kein Turnier</div>
            <div style={{ fontSize: 13 }}>Erstellen Sie ein Liga- oder K.O.-Turnier für Ihre Veranstaltungen.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tournaments.map(t => {
              const fi = FORMAT_INFO[t.format] || FORMAT_INFO.liga;
              const si = STATUS_INFO[t.status] || STATUS_INFO.active;
              return (
                <div key={t.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{t.name}</div>
                    {t.description && (
                      <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>{t.description}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: fi.bg, color: fi.color }}>
                        {fi.label}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: si.bg, color: si.color }}>
                        {si.label}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 56 }}>
                    <div style={{ fontWeight: 800, fontSize: 22, color: 'var(--primary)' }}>{t.round_count}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Runden</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', minWidth: 70, textAlign: 'right' }}>
                    {fmt(t.created_at)}
                  </div>
                  <Link to={`/admin/tournaments/${t.id}`} className="btn btn-sm btn-primary">
                    Öffnen
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
